import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';

import type { BizyPeopleBrowserEntry, BizyPeopleBrowserResponse, BizyPersonMomentsResponse } from '../lib/bizy/types';
import { bizyUnreadNotificationsQueryKey } from './bizy-notifications';
import { headers } from './meta';

// Member-facing People Browser data layer (BIZY-635), modeled on
// `src/queries/bizy-conversations.ts`. Endpoint contracts are FE-proposed on
// BIZY-632 and may shift as the backend lands.
//
// The shared `headers` const adds `Key-Inflection: camel`, so responses arrive
// camelCase (matching the `BizyPeople*` types).

// ─── Query Keys ──────────────────────────────────────────────────────────────

enum QueryKey {
	People = 'bizy-people',
	PersonMoments = 'bizy-person-moments',
}

export const bizyPeopleQueryKey = () => [QueryKey.People];

export const bizyPersonMomentsQueryKey = (personId?: string) => [QueryKey.PersonMoments, personId].filter(Boolean);

// ─── Path helpers ────────────────────────────────────────────────────────────

export const bizyPeoplePath = (prefix = '') => `${prefix}/bizy/people`;
export const bizyPersonMomentsPath = (personId: string, prefix = '') => `${prefix}/bizy/people/${personId}/moments`;
export const bizyPersonEntrySeenPath = (notificationId: string, prefix = '') => `${prefix}/bizy/people/entries/${notificationId}/seen`;
export const bizyPersonEntrySkipPath = (notificationId: string, prefix = '') => `${prefix}/bizy/people/entries/${notificationId}/skip`;

// ─── API response types ──────────────────────────────────────────────────────

interface BizyMarkSeenResponse {
	notificationId: string;
	openedAt: string;
}

interface BizySkipResponse {
	notificationId: string;
	dismissedAt: string;
}

// ─── Query hooks ─────────────────────────────────────────────────────────────

/**
 * Fetches the People Browser sidebar list (at most 5 entries,
 * newest-surfaced-first, one entry per moment).
 *
 * GET /api/v2/bizy/people
 */
export const useBizyPeopleBrowserEntries = (options?: { enabled?: boolean }) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: bizyPeopleQueryKey(),
		queryFn: async (): Promise<BizyPeopleBrowserEntry[]> => {
			const res = await http.get<BizyPeopleBrowserResponse>(bizyPeoplePath(), { headers });
			return res.data.people;
		},
		enabled: options?.enabled ?? true,
	});
};

/**
 * Fetches one person's full moment history for the detail card.
 *
 * GET /api/v2/bizy/people/:personId/moments
 */
export const usePersonMoments = (personId: string | undefined) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: bizyPersonMomentsQueryKey(personId),
		queryFn: async (): Promise<BizyPersonMomentsResponse> => {
			// personId is guaranteed by `enabled: !!personId` below
			const res = await http.get<BizyPersonMomentsResponse>(bizyPersonMomentsPath(personId as string), { headers });
			return res.data;
		},
		enabled: !!personId,
	});
};

// ─── Mutation hooks ──────────────────────────────────────────────────────────

/**
 * Marks a People Browser entry as seen/opened (sets `opened_at`).
 *
 * POST /api/v2/bizy/people/entries/:notificationId/seen
 */
export const useBizyMarkMomentSeenMutation = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (notificationId: string): Promise<BizyMarkSeenResponse> => {
			const res = await http.post<BizyMarkSeenResponse>(bizyPersonEntrySeenPath(notificationId), {}, { headers });
			return res.data;
		},
		onSuccess: () => {
			// Marking a moment seen changes unread state too — refresh the
			// navbar badge / peek source alongside the sidebar list.
			queryClient.invalidateQueries(bizyPeopleQueryKey());
			queryClient.invalidateQueries(bizyUnreadNotificationsQueryKey());
		},
	});
};

/**
 * Skips a People Browser entry (sets `dismissed_at`), removing it from the
 * sidebar.
 *
 * POST /api/v2/bizy/people/entries/:notificationId/skip
 */
export const useBizySkipMomentMutation = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (notificationId: string): Promise<BizySkipResponse> => {
			const res = await http.post<BizySkipResponse>(bizyPersonEntrySkipPath(notificationId), {}, { headers });
			return res.data;
		},
		onSuccess: () => {
			// Skipping a moment removes it from the sidebar and clears its
			// unread — refresh both caches.
			queryClient.invalidateQueries(bizyPeopleQueryKey());
			queryClient.invalidateQueries(bizyUnreadNotificationsQueryKey());
		},
	});
};
